import { useRef, useEffect, useState, forwardRef, useImperativeHandle, SyntheticEvent } from 'react';
import Artist from './artist';
import ArtistWebgl, { AudioReactiveParameters, Sampler, StateType } from './artistWebgl';
import Animator from './animator';
import Layout, { LayoutMessage } from './layout';
import defineViewMode from './viewMode';
import { PointVector2D } from './geometry';
import { Page } from './App';
import { Box, Button, Card, CardActionArea, CardContent, CardMedia, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Drawer, FormControl, IconButton, MenuItem, OutlinedInput, Paper, Select, SelectChangeEvent, Slider, Switch, Tab, Tabs, TextField, ToggleButton, Tooltip, Typography } from '@mui/material';
import { ExpandLess, ExpandMore, MusicNote, SportsEsports, Videocam, Settings } from '@mui/icons-material';
import { alphaChannelFs } from './shaders';
import { SceneParameters, scenes } from './scenes';
import './main.css'
import FFT from './audioAnalyser';
import QRCode from "react-qr-code";
import io, { Socket } from 'socket.io-client';
import { AudioSettingUpdate, AudioSettings, CurrentParameters, GeneralSettings, MessagePayload, ParameterUpdate } from './remoteControl';

interface MainPageProps {
    setPage: (page: Page) => void;
}

const Main = ({setPage}: MainPageProps) => {
    const [bg, setBg] = useState<Artist | null>(null);
    const [cursor, setCursor] = useState<Artist | null>(null);

    const bottomMenuRef = useRef<BottomMenuRef>(null);
    const sceneIdRef = useRef(0);
    let scene = scenes[sceneIdRef.current];

    const animator = new Animator(false);
    const bgCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const cursorCanvasRef = useRef<HTMLCanvasElement | null>(null);
    
    let videoStream = document.createElement('video');
    let audioStream = document.createElement('audio');

    let viewMode = defineViewMode();

    const layoutCanvas = document.createElement("canvas");
    const layoutArtist = new Artist(layoutCanvas);
    const layout = new Layout(layoutArtist, animator.elapsed, 'main');

    const webglCanvasBg = document.createElement("canvas");
    const WebglBgSamplers = scene.samplers;
    WebglBgSamplers.forEach(sampler => {
        switch(sampler.sampleType) {
            case 'Self':
                break;
            case 'Canvas':
                break;
            case 'Camera':
                sampler.sampleSource = videoStream
                break;
            case 'Function':
                break;
            case 'BodySegmentation':
                sampler.sampleSource = videoStream
                break;
        }
    })
    const artistWebglBg = new ArtistWebgl(webglCanvasBg, scene.vertexShader, scene.fragmentShader, scene.parameters, layoutCanvas, WebglBgSamplers, scene.postProcessing, scene.shapes?scene.shapes():undefined);

    setTimeout(() => {
        if (sceneIdRef.current == scene.id && scene.fadeInToNormalTransition) {
            artistWebglBg.changeState(StateType.Normal, animator.elapsed - scene.fadeInTime);
        }
    }, scenes[sceneIdRef.current].fadeInTime);

    const webglCanvasParticle = document.createElement("canvas");
    const WebglParticleSamplers = scene.cursor.samplers;
    WebglParticleSamplers.forEach(sampler => {
        switch(sampler.sampleType) {
            case 'Self':
                break;
            case 'Canvas':
                sampler.sampleSource = webglCanvasBg;
                break;
            case 'Camera':
                sampler.sampleSource = videoStream
                break;
            case 'Function':
                break;
            case 'BodySegmentation':
                sampler.sampleSource = videoStream
                break;
        }
    })
    const artistWebglParticle = new ArtistWebgl(webglCanvasParticle, scene.cursor.vertexShader, scene.cursor.fragmentShader, scene.parameters, undefined, WebglParticleSamplers, scene.cursor.postProcessing, scene.cursor.shapes?scene.cursor.shapes():undefined);

    const webglCanvasParticleAlpha = document.createElement("canvas");
    const webglParticleAlphaSamplers: Array<Sampler> = [
        {
            states: [StateType.FadeIn, StateType.Normal, StateType.FadeOut],
            sampleType: 'Canvas',
            shapeSampler: false,
            sampleSource: artistWebglParticle.canvas,
        }
    ]
    const artistWebglParticleAlpha = new ArtistWebgl(webglCanvasParticleAlpha, undefined, alphaChannelFs, undefined, undefined, webglParticleAlphaSamplers);

    let cursorStillTime = 0;
    let pointerDown = false;
    let touchStartY = 0;
    let touchCurrentY = 0;
    let pointedLink: HTMLAnchorElement;

    let audioContext: AudioContext;
    let fft: FFT;


    function openFullscreen() {
        const elem = document.documentElement as any;
        if (elem.requestFullscreen) {
            elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) { /* Safari */
        elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) { /* IE11 */
        elem.msRequestFullscreen();
        }
    }

    function closeFullscreen() {
        const doc = document as any;
        if (doc.exitFullscreen) {
            doc.exitFullscreen();
        } else if (doc.webkitExitFullscreen) { /* Safari */
            doc.webkitExitFullscreen();
        } else if (doc.msExitFullscreen) { /* IE11 */
            doc.msExitFullscreen();
        }
    }

    const handleSceneChange = (sceneId: number) => {
        sceneIdRef.current = sceneId;
        if (!bottomMenuRef.current!.skipTransitionsOn) {
            layout.changeState('FadeOut', animator.elapsed);
            setTimeout(() => {
                if (sceneIdRef.current == sceneId) {
                    artistWebglBg.changeState(StateType.FadeOut, animator.elapsed);
                }
            }, 500);
            setTimeout(() => {
                if (sceneIdRef.current == sceneId) {
                    scene = scenes[sceneId];
                    scene.samplers.forEach(sampler => {
                        switch(sampler.sampleType) {
                            case 'Self':
                                break;
                            case 'Canvas':
                                break;
                            case 'Camera':
                                sampler.sampleSource = videoStream
                                break;
                            case 'Function':
                                break;
                            case 'BodySegmentation':
                                sampler.sampleSource = videoStream
                                break;
                        }
                    })
                    scene.cursor.samplers.forEach(sampler => {
                        switch(sampler.sampleType) {
                            case 'Self':
                                break;
                            case 'Canvas':
                                sampler.sampleSource = webglCanvasBg;
                                break;
                            case 'Camera':
                                sampler.sampleSource = videoStream
                                break;
                            case 'Function':
                                break;
                            case 'BodySegmentation':
                                sampler.sampleSource = videoStream
                                break;
                        }
                    })
                    layout.changeState('FadeIn', animator.elapsed);
                    artistWebglBg.changeState(StateType.FadeIn, animator.elapsed);
                    artistWebglBg.reloadShader(scene.vertexShader, scene.fragmentShader, scene.parameters, scene.samplers, scene.postProcessing, scene.shapes?scene.shapes():undefined);
                    artistWebglParticle.reloadShader(scene.cursor.vertexShader, scene.cursor.fragmentShader, scene.parameters, scene.cursor.samplers, scene.cursor.postProcessing, scene.cursor.shapes?scene.cursor.shapes():undefined);
                }
            }, scene.fadeOutTime + 500);
            setTimeout(() => {
                if (sceneIdRef.current == sceneId && scene.fadeInToNormalTransition) {
                    scene = scenes[sceneId];
                    artistWebglBg.changeState(StateType.Normal, animator.elapsed - scene.fadeInTime);
                }
            }, scene.fadeOutTime + scenes[sceneId].fadeInTime + 500);
        } else {
            scene = scenes[sceneId];
            scene.samplers.forEach(sampler => {
                switch(sampler.sampleType) {
                    case 'Self':
                        break;
                    case 'Canvas':
                        break;
                    case 'Camera':
                        sampler.sampleSource = videoStream
                        break;
                    case 'Function':
                        break;
                    case 'BodySegmentation':
                        sampler.sampleSource = videoStream
                        break;
                }
            })
            scene.cursor.samplers.forEach(sampler => {
                switch(sampler.sampleType) {
                    case 'Self':
                        break;
                    case 'Canvas':
                        sampler.sampleSource = webglCanvasBg;
                        break;
                    case 'Camera':
                        sampler.sampleSource = videoStream
                        break;
                    case 'Function':
                        break;
                    case 'BodySegmentation':
                        sampler.sampleSource = videoStream
                        break;
                }
            })
            //layout.changeState('FadeIn', animator.elapsed);
            artistWebglBg.changeState(StateType.Normal, animator.elapsed);
            artistWebglBg.reloadShader(scene.vertexShader, scene.fragmentShader, scene.parameters, scene.samplers, scene.postProcessing, scene.shapes?scene.shapes():undefined);
            artistWebglParticle.reloadShader(scene.cursor.vertexShader, scene.cursor.fragmentShader, scene.parameters, scene.cursor.samplers, scene.cursor.postProcessing, scene.cursor.shapes?scene.cursor.shapes():undefined);
        }
    }

    const handleCameraStatusChange = (cameraOn: boolean, selectedCameraId?: string) => {
        if (cameraOn) {
            navigator.mediaDevices.getUserMedia({ 
                video: selectedCameraId? { deviceId: { exact: selectedCameraId }} : true
            })
            .then(stream => {
                videoStream.srcObject = stream;
                videoStream.play();
            })
            .catch(err => console.error("Error accessing media devices: ", err));
        } else if (videoStream.srcObject) {
            const tracks = (videoStream.srcObject as MediaStream).getTracks();
            tracks.forEach(track => track.stop());
            videoStream.srcObject = null;
        }
    }

    const handleMicrophoneStatusChange = (microphoneOn: boolean, selectedMicrophoneId?: string) => {
        if (microphoneOn) {
            navigator.mediaDevices.getUserMedia({ 
                audio: selectedMicrophoneId? { deviceId: { exact: selectedMicrophoneId }} : true
            })
            .then(stream => {
                audioStream.srcObject = stream;
                //audioStream.play();

                audioContext = new AudioContext();
                fft = new FFT( 0.8, 1024, audioContext);

                const source = audioContext.createMediaStreamSource(stream);
                fft.setInput(source);
                //source.connect(analyser);
            })
            .catch(err => console.error("Error accessing media devices: ", err));
        } else if (audioStream.srcObject) {
            const tracks = (audioStream.srcObject as MediaStream).getTracks();
            tracks.forEach(track => track.stop());
            audioStream.srcObject = null;
        }
    }

    window.onresize = function() { 
        if (bg && cursor) {
            viewMode = defineViewMode();
            bg.resizeImageToCanvasSize();
            cursor.resizeImageToCanvasSize();
            layoutArtist.updateCanvasSize(bg.canvasWidth, bg.canvasHeight);
            artistWebglBg.updateCanvasSize(bg.canvasWidth, bg.canvasHeight);
            artistWebglParticle.updateCanvasSize(bg.canvasWidth, bg.canvasHeight);
            artistWebglParticleAlpha.updateCanvasSize(bg.canvasWidth, bg.canvasHeight);
        }
    }
    useEffect(() => {
        if (bgCanvasRef.current && cursorCanvasRef.current) {
            setBg(new Artist(bgCanvasRef.current));
            setCursor(new Artist(cursorCanvasRef.current));
        }
    }, []);
    useEffect(() => {
        if (bg && cursor) {
            (window as any).record = (frames: number, delay: number, fps: number) => animator.record(frames, delay, webglCanvasBg, fps);
            bg.resizeImageToCanvasSize();
            cursor.resizeImageToCanvasSize();
            layout.cursor = cursor?.canvas;
            layoutArtist.updateCanvasSize(bg.canvasWidth, bg.canvasHeight);
            artistWebglBg.updateCanvasSize(bg.canvasWidth, bg.canvasHeight);
            artistWebglParticle.updateCanvasSize(bg.canvasWidth, bg.canvasHeight);
            artistWebglParticleAlpha.updateCanvasSize(bg.canvasWidth, bg.canvasHeight);
            bg.createFlowfield();
            bg.flowfield!.setEdgesToCenter();
            bg.flowfield!.createParticles(1, 2, false);

            document.onpointerdown = (event) => {
                pointerDown = true;
                const cursorAnimation = viewMode.size == 'desktop' && viewMode.graphics == 'high' && bottomMenuRef.current!.cursorAnimationOn;
                const cursorLocation = (cursorAnimation) ? 
                    {x: bg.flowfield!.particles[0].location.x, y: bg.flowfield!.particles[0].location.y} :
                    {x: event.clientX, y: event.clientY}
                cursor.canvas.style.pointerEvents = 'none';
                const link = cursorLocation.x && cursorLocation.y? document.elementFromPoint(cursorLocation.x, cursorLocation.y) as HTMLAnchorElement: undefined;
                cursor.canvas.style.pointerEvents = 'auto';
                if (link && link.tagName === 'A') {
                    link.click();
                }
                if (layout.fullScreenButtonOver) {
                    if(!document.fullscreenElement){ 
                        openFullscreen();
                    } else { 
                        closeFullscreen();
                    }
                }
                if (layout.logoOver) {
                    layout.changeState('FadeOut', animator.elapsed);
                    setTimeout(() => {
                        artistWebglBg.changeState(StateType.FadeOut, animator.elapsed);
                    }, 500)
                    setTimeout(() => {
                        animator.animationPause();
                        setPage('Landing');
                    }, scene.fadeOutTime + 500)
                }
            }
            document.onpointerup = (event) => {
                pointerDown = false;
                layout.scrollDragging = false;
            }
            document.onpointermove = (event) => {
                const cursorAnimation = viewMode.size == 'desktop' && viewMode.graphics == 'high' && bottomMenuRef.current!.cursorAnimationOn;
                const cursorLocation = (cursorAnimation) ? 
                    {x: bg.flowfield!.particles[0].location.x, y: bg.flowfield!.particles[0].location.y} :
                    {x: event.clientX, y: event.clientY}
                cursor.canvas.style.pointerEvents = 'none';
                const link = cursorLocation.x && cursorLocation.y? document.elementFromPoint(cursorLocation.x, cursorLocation.y) as HTMLAnchorElement: undefined;
                cursor.canvas.style.pointerEvents = 'auto';
                if (link && link.tagName === 'A') {
                    if (link !== pointedLink) {
                        pointedLink?.classList.remove('hover');
                        pointedLink = link;
                    }
                    link.classList.add('hover');
                } else {
                    pointedLink?.classList.remove('hover');
                }
                cursorStillTime = 0;
                bg.flowfield?.setParticlesFollowCursor(true);
                artistWebglBg.updateCursorPosition(cursorLocation.x, cursorLocation.y);
                artistWebglParticle.updateCursorPosition(event.clientX, event.clientY);
                bg.updateCursorPosition(event.clientX, event.clientY);
                if (layout.scrollDragging == true) {
                    layout.setScrollValueByPointerLocation(event.clientY);
                }
            }
            document.onpointerleave = (event) => {
                pointerDown = false;
                bg.flowfield?.setParticlesFollowCursor(false);
            }
            document.onwheel = (event) => {
                layout.addToScrollValue(event.deltaY);
            }
            document.ontouchstart = (event) => {
                event.preventDefault();
                const cursorAnimation = viewMode.size == 'desktop' && viewMode.graphics == 'high' && bottomMenuRef.current!.cursorAnimationOn;
                const cursorLocation = (cursorAnimation) ? 
                    {x: bg.flowfield!.particles[0].location.x, y: bg.flowfield!.particles[0].location.y} :
                    {x: event.touches[0].clientX, y: event.touches[0].clientY}
                cursor.canvas.style.pointerEvents = 'none';
                const link = cursorLocation.x && cursorLocation.y? document.elementFromPoint(cursorLocation.x, cursorLocation.y) as HTMLAnchorElement: undefined;
                cursor.canvas.style.pointerEvents = 'auto';
                if (link && link.tagName === 'A') {
                    link.click();
                }
                if (layout.scrollPosition.distanceTo(new PointVector2D(event.touches[0].clientX, event.touches[0].clientY)) < 6) {
                    layout.scrollDragging = true;
                }
                touchStartY = event.touches[0].clientY;
            }
            document.ontouchmove = (event) => {
                event.preventDefault();
                touchCurrentY = event.touches[0].clientY;
                let touchScrollAmount = touchStartY - touchCurrentY;
                layout.addToScrollValue(touchScrollAmount);
                touchStartY = touchCurrentY;
                if (layout.scrollDragging == true) {
                    layout.setScrollValueByPointerLocation(event.touches[0].clientY);
                }
            }
            document.ontouchend = (event) => {
                event.preventDefault();
                layout.scrollDragging = false;
            }
            animator.animationSequence = () => {
                cursorStillTime += 1;
                if (cursorStillTime > animator.fps * 10) bg.flowfield?.setParticlesFollowCursor(false);
                if (animator.fps) {
                    const newViewMode = defineViewMode(animator.fps);
                    if (newViewMode.graphics != viewMode.graphics && newViewMode.graphics != 'high') {
                        bottomMenuRef.current!.updateCursorAnimationOn(false);
                    }
                    viewMode = newViewMode;
                }
                const cursorAnimation = viewMode.size == 'desktop' && viewMode.graphics == 'high' && bottomMenuRef.current!.cursorAnimationOn;
                if (!cursorAnimation) {
                    cursor.clear();
                    //cursor.canvas.style.pointerEvents = 'none';
                    
                    layout.setCursorOverride('auto');
                    //bg.canvas.style.cursor = "auto";
                    //cursor.canvas.style.cursor = "auto";
                } else {
                    //cursor.canvas.style.pointerEvents = 'auto';
                    layout.setCursorOverride(null);
                    //bg.canvas.style.cursor = "none";
                    //cursor.canvas.style.cursor = "none";
                }
                

                //layout.artist.clear();
                //console.log(videoStream.videoWidth, videoStream.videoHeight)

                let audioReactiveParameters: AudioReactiveParameters | undefined;
                if (audioStream && audioStream.srcObject && audioContext && scenes[sceneIdRef.current].audioReactive) {
                    fft.analyze();
                    const settings = bottomMenuRef.current!.microphoneSettings;
                    fft.detectBeat(settings.frequencyRange[0], settings.frequencyRange[1], settings.threshold, animator.fps * 60 / settings.bpm);
                    audioReactiveParameters = {
                        beatDetected: fft.peakIsDetected,
                        beatCount: fft.beatCount,
                        beatFade: fft.beatFade,
                        energy: fft.energy,
                        levels: fft.freqDomain,
                    }
                }

                artistWebglBg.updatePointerPressure(pointerDown, animator.fps);
                artistWebglBg.drawScene(animator.elapsed, bg.flowfield?.particles[0].location.x, bg.flowfield?.particles[0].location.y, audioReactiveParameters);
                bg.drawImage(artistWebglBg.canvas);
                //bg.drawText(animator.fps.toString());
                
                const message: LayoutMessage | undefined = !bottomMenuRef.current!.cameraOn && scene.cameraRequired ? 'cameraAccessRequired' : undefined;

                if (cursorAnimation) {

                    layout.setLayoutType('main');
                    layout.draw(animator.elapsed, bg.flowfield!.particles[0].location.x, bg.flowfield!.particles[0].location.y, message);
                    cursor.clear();
                    artistWebglParticle.drawScene(animator.elapsed, bg.flowfield?.particles[0].location.x, bg.flowfield?.particles[0].location.y);
                    if (!scene.cursor.alphaFilteringRequired) {
                        cursor.drawImage(artistWebglParticle.canvas);
                    } else {
                        artistWebglParticleAlpha.drawScene(animator.elapsed);
                        cursor.drawImage(artistWebglParticleAlpha.canvas);
                    }
                    bg.flowfield?.setTime(animator.elapsed / 500);
                    bg.flowfield?.moveParticles(animator.fps, bg.cursorX, bg.cursorY);
                }
                else {
                    layout.setLayoutType('main');
                    layout.draw(animator.elapsed, bg.cursorX, bg.cursorY, message);
                } 
            }
            animator.animationStart();
        }
        return () => {
            document.onpointerdown = null;
            document.onpointerup = null;
            document.onpointermove = null;
            document.onpointerleave = null;
            document.onwheel = null;
            document.ontouchstart = null;
            document.ontouchmove = null;
            document.ontouchend = null;
        };
    }, [bg]);
    return (
        <>
            <canvas ref={bgCanvasRef} style={{width: "100vw", height: "100dvh", position: 'absolute', zIndex: 0}}/>
            <canvas ref={cursorCanvasRef} style={{width: "100vw", height: "100dvh", position: 'absolute', zIndex: 2, opacity: 0}}/>
            <Button onClick={() => bottomMenuRef.current!.updateDrawerOpen(true)} style={{...drawerButtonStyle, bottom: 0}}>
                <ExpandLess/>
            </Button>
            <BottomMenu ref={bottomMenuRef} sceneIdRef={sceneIdRef} handleSceneChange={handleSceneChange} handleCameraStatusChange={handleCameraStatusChange} handleMicrophoneStatusChange={handleMicrophoneStatusChange}/>
        </>
    )
}

export default Main

//type DeviceType = 'camera' | 'microphone';

const drawerButtonStyle: React.CSSProperties = {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 100,
    zIndex: 4,
    backgroundColor: 'rgb(70, 70, 70)',
    color: 'white',
    borderRadius: '5px 5px 0px 0px'
}

const drawerPaperStyle: React.CSSProperties = {
    position: 'absolute',
    overflow: 'visible',
    bottom: 0,
    pointerEvents: 'auto',
    backgroundColor: 'rgb(70, 70, 70)',
    color: 'white'
}

interface BottomMenuRef {
    updateDrawerOpen: (open: boolean) => void;
    updateCursorAnimationOn: (cursorOn: boolean) => void;
    cameraOn: boolean;
    cameraId: string | undefined;
    microphoneOn: boolean;
    microphoneId: string | undefined;
    cursorAnimationOn: boolean;
    skipTransitionsOn: boolean;
    microphoneSettings: MicrophoneSettings;
}

interface BottomMenuProps {
    sceneIdRef: React.MutableRefObject<number>;
    handleSceneChange: (scene: number) => void;
    handleCameraStatusChange: (cameraOn: boolean, cameraId?: string) => void;
    handleMicrophoneStatusChange: (microphoneOn: boolean, microphoneId?: string) => void;
}

function a11yProps(index: number) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}

const BottomMenu = forwardRef(({sceneIdRef, handleSceneChange, handleCameraStatusChange, handleMicrophoneStatusChange}: BottomMenuProps, ref) => {
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [cameraOn, setCameraOn] = useState<boolean>(false);
    const [cameraId, setCameraId] = useState<string>('');
    const [microphoneOn, setMicrophoneOn] = useState<boolean>(false);
    const [microphoneId, setMicrophoneId] = useState<string | undefined>();
    const [cursorAnimationOn, setCursorAnimationOn] = useState<boolean>(true);
    const [skipTransitionsOn, setSkipTransitionsOn] = useState<boolean>(false);
    const updateDrawerOpen = (open: boolean) => setDrawerOpen(open);
    const updateCursorAnimationOn = (cursorOn: boolean) => setCursorAnimationOn(cursorOn);
    useImperativeHandle(ref, (): BottomMenuRef => ({
        updateDrawerOpen, updateCursorAnimationOn, cameraOn, cameraId, microphoneOn, microphoneId, cursorAnimationOn, skipTransitionsOn, microphoneSettings
    }));

    const [mediaDevicesAvailable, setMediaDevicesAvailable] = useState(false);
    useEffect(() => {
        setMediaDevicesAvailable(!!(navigator.mediaDevices && navigator.mediaDevices.enumerateDevices));
    }, []);

    //const [deviceSettingsDialogOpen, setDeviceSettingsDialogOpen] = useState<{open: boolean, initial: string | undefined}>({open: false, initial: undefined});
    const [microphoneSettingsDialogOpen, setMicrophoneSettingsDialogOpen] = useState(false);
    const [microphoneSettings, setMicrophoneSettings] = useState<MicrophoneSettings>({
        currentDevice: '',
        bpm: 150,
        threshold: 0.4,
        frequencyRange: [20, 140]
    });
    const [cameraSettingsDialogOpen, setCameraSettingsDialogOpen] = useState(false);

    //const [deviceType, setDeviceType] = useState<DeviceType>('camera');

    const [remoteControlDialogOpen, setRemoteControlDialogOpen] = useState(false);

    /* const handleDeviceSettingsDialogClose = (deviceId: string | undefined) => {
        handleDeviceChange(deviceId);
        setDeviceSettingsDialogOpen({open: false, initial: undefined});
    }; */

    const handleMicrophoneSettingsDialogClose = () => {
        setMicrophoneSettingsDialogOpen(false);
    };

    const handleCameraSettingsDialogClose = (deviceId: string) => {
        handleCameraChange(deviceId);
        setCameraSettingsDialogOpen(false);
    };

    const handleRemoteControlDialogClose = () => {
        setRemoteControlDialogOpen(false);
    };

    /* const handleDeviceChange = (deviceId: string | undefined) => {
        if (deviceType == 'camera') {
            setCameraId(deviceId);
            handleCameraStatusChange(cameraOn, deviceId);
        }
        else {
            setMicrophoneId(deviceId);
            handleMicrophoneStatusChange(microphoneOn, deviceId);
        }
    } */

    const handleMicrophoneChange = (deviceId: string | undefined) => {
        setMicrophoneId(deviceId);
        handleMicrophoneStatusChange(microphoneOn, deviceId);
    }

    const handleCameraChange = (deviceId: string) => {
        setCameraId(deviceId);
        handleCameraStatusChange(cameraOn, deviceId);
    }

    const [tab, setTab] = useState(0);
    const [hasClickedParameters, setHasClickedParameters] = useState(false);


    const handleTabChange = (event: SyntheticEvent, newValue: number) => {
        setTab(newValue);
        if (newValue === 1 && !hasClickedParameters) {
            setHasClickedParameters(true);
        }
    };

    /* const handleDeviceSettingsClick = (type: DeviceType) => {
        setDeviceType(type);
        let initialDeviceId: {open: boolean, initial: string | undefined} = {open: true, initial: undefined};
        if (type == 'camera') {
            initialDeviceId.initial = cameraId;
        } else {
            initialDeviceId.initial = microphoneId;
        }
        setDeviceSettingsDialogOpen(initialDeviceId);
    }; */

    const handleMicrophoneSettingsClick = () => {
        let initialDeviceId: {open: boolean, initial: string | undefined} = {open: true, initial: undefined};
        setMicrophoneSettingsDialogOpen(true);
    };

    const handleCameraSettingsClick = () => {
        setCameraSettingsDialogOpen(true);
    };

    
    /* const [peerConnection, setPeerConnection] = useState<RTCPeerConnection>();
    const [dataChannel, setDataChannel] = useState<RTCDataChannel>();
    const [offer, setOffer] = useState<string>();
    const [answer, setAnswer] = useState<string>();
    const [connectionStatus, setConnectionStatus] = useState<string>('Not connected');

    useEffect(() => {
        const fetchTurnCredentials = async () => {
            const response = await fetch("https://vjonline.metered.live/api/v1/turn/credentials?apiKey=83cdb78dc2b887182b1c8bb679471cf1220e");
            const iceServers = await response.json();
            return iceServers;
        };

        //fetchTurnCredentials().then(iceServers => {
            const peerConnection = new RTCPeerConnection({ iceServers: [
                {
                  urls: "stun:stun.relay.metered.ca:80",
                },
                {
                  urls: "turn:global.relay.metered.ca:80",
                  username: "185a4b90ba32fae69c922ed3",
                  credential: "b5EQ+W5kB/VR7Rcg",
                }]
            });
            const dataChannel = peerConnection.createDataChannel('remoteControl');
            dataChannel.onopen = () => {
                console.log('Data channel is open');
            };
            dataChannel.onmessage = (event) => {
                console.log('Received message:', event.data);
            };

            peerConnection.onicecandidate = (event) => {
                if (event.candidate) {
                    console.log('ICE Candidate:', JSON.stringify(event.candidate));
                }
            };

            peerConnection.oniceconnectionstatechange = () => {
                console.log('ICE connection state:', peerConnection.iceConnectionState);
                setConnectionStatus(`ICE connection state: ${peerConnection.iceConnectionState}`);
            };
            setPeerConnection(peerConnection);
            setDataChannel(dataChannel);
            peerConnection.createOffer().then((offer) => {
                return peerConnection.setLocalDescription(offer);
            })
        //})

        return () => {
            peerConnection?.close();
        };
    }, []); */

    
    function generateSecureId(): string {
        const array = new Uint32Array(4);
        window.crypto.getRandomValues(array);
        return Array.from(array, dec => dec.toString(16)).join('');
    }

    const socketRef = useRef<Socket>();
    const sessionIdRef = useRef<string>();
    const [sessionLink, setSessionLink] = useState<string>();

    const [scene, setScene] = useState(sceneIdRef.current);
    const [sceneParameters, setSceneParameters] = useState(scenes[scene].parameters);

    const sceneRef = useRef(scene);
    const generalSettingsRef = useRef<GeneralSettings>({
        camera: cameraOn,
        microphone: microphoneOn,
        cursorAnimation: cursorAnimationOn,
        skipTransitions: skipTransitionsOn
    })
    const microphoneSettingsRef = useRef<MicrophoneSettings>(microphoneSettings);

    useEffect(() => {
        sceneRef.current = scene;
        generalSettingsRef.current = {
            camera: cameraOn,
            microphone: microphoneOn,
            cursorAnimation: cursorAnimationOn,
            skipTransitions: skipTransitionsOn
        };
        microphoneSettingsRef.current = microphoneSettings;
    }, [scene, cameraOn, microphoneOn, cursorAnimationOn, skipTransitionsOn, microphoneSettings]);

    const handleSendCurrentParameters = (currentGeneralSettings: GeneralSettings, currentMicrophoneSettings: MicrophoneSettings, currentScene: number, currentSceneParameters: [] | SceneParameters) => {
        if (socketRef.current && sessionIdRef.current && generalSettingsRef.current) {
            let audioSettings = undefined;
            if (scenes[currentScene].audioReactive && currentGeneralSettings.microphone) {
                audioSettings = {
                    bpm: currentMicrophoneSettings.bpm,
                    threshold: currentMicrophoneSettings.threshold,
                    frequencyRange: currentMicrophoneSettings.frequencyRange
                }
            }
            const currentParameters: CurrentParameters = {
                generalSettings: currentGeneralSettings,
                audioSettings,
                scene: currentScene,
                sceneParameters: currentSceneParameters,
            };
            const newPayload = {
                sessionId: sessionIdRef.current, 
                messageType: 'currentParameters',
                message: JSON.stringify(currentParameters)
            };
            socketRef.current.emit('message', newPayload);
        }
    }

    const handleSetSceneParameters = (boxIndex: number, parameterIndex: number, value: number) => {
        scenes[sceneRef.current].parameters[boxIndex].parameters[parameterIndex].value = value;
        setSceneParameters(prevParameters => {
            const updatedParameters = [...prevParameters];
            updatedParameters[boxIndex] = {
                ...updatedParameters[boxIndex],
                parameters: updatedParameters[boxIndex].parameters.map((param, index) => {
                    if (index === parameterIndex) {
                        // Returning a new object with the updated value
                        return { ...param, value: value };
                    }
                    return param;
                })
            };
            handleSendCurrentParameters(generalSettingsRef.current, microphoneSettingsRef.current, sceneRef.current, updatedParameters);
            return updatedParameters;
        });
    }

    const handleMicrophoneSettingsUpdate = (type: keyof MicrophoneSettings, value: number | number[] | string) => {
        const currentMicrophoneSettings = {
            ...microphoneSettingsRef.current,
            [type]: value
        }
        setMicrophoneSettings(currentMicrophoneSettings);
        if (type === 'currentDevice') {
            handleMicrophoneChange(value as string);
        } else {
            handleSendCurrentParameters(generalSettingsRef.current, currentMicrophoneSettings, sceneRef.current, scenes[sceneRef.current].parameters);
        }
    };

    const handleCardClick = (sceneId: number) => {
        setScene(sceneId);
        setSceneParameters(scenes[sceneId].parameters);
        handleSendCurrentParameters(generalSettingsRef.current, microphoneSettingsRef.current, sceneId, scenes[sceneId].parameters);
        handleSceneChange(sceneId);
        setDrawerOpen(false);
    };

    const handleCameraButtonClick = (state: boolean) => {
        const currentSettings = {
            ...generalSettingsRef.current,
            'camera': state
        }
        handleSendCurrentParameters(currentSettings, microphoneSettingsRef.current, sceneRef.current, scenes[sceneRef.current].parameters);
        handleCameraStatusChange(state, cameraId);
        setCameraOn(state);
    }

    const handleMicrophoneButtonClick = (state: boolean) => {
        const currentSettings = {
            ...generalSettingsRef.current,
            'microphone': state
        }
        handleSendCurrentParameters(currentSettings, microphoneSettingsRef.current, sceneRef.current, scenes[sceneRef.current].parameters);
        handleMicrophoneStatusChange(state, microphoneSettingsRef.current!.currentDevice);
        setMicrophoneOn(state);
    }

    const handleCursorAnimationButtonClick = (state: boolean) => {
        const currentSettings = {
            ...generalSettingsRef.current,
            'cursorAnimation': state
        }
        handleSendCurrentParameters(currentSettings, microphoneSettingsRef.current, sceneRef.current, scenes[sceneRef.current].parameters);
        setCursorAnimationOn(state);
    }

    const handleSkipTransitionsButtonClick = (state: boolean) => {
        const currentSettings = {
            ...generalSettingsRef.current,
            'skipTransitions': state
        }
        handleSendCurrentParameters(currentSettings, microphoneSettingsRef.current, sceneRef.current, scenes[sceneRef.current].parameters);
        setSkipTransitionsOn(state);
    }
    
    useEffect(() => {
        if (sessionIdRef.current) {
            const link = 'https://api.parametric-jam.com/remote';   // 'http://localhost:8001/remote' // https://fragrance-fourth-density-sept.trycloudflare.com/remote';
            const socket = io(link, {
                query: { sessionId: sessionIdRef.current }
            });
            socketRef.current = socket;

            socket.on('connect', () => {
                console.log('Socket connected');
                socket.emit('register', { sessionId: sessionIdRef.current });
            });

            socket.on('message', (payload: MessagePayload) => {
                switch(payload.messageType) {
                    case 'command':
                        if (payload.message === 'getCurrentParameters') {
                            handleSendCurrentParameters(generalSettingsRef.current, microphoneSettingsRef.current, sceneRef.current, scenes[sceneRef.current].parameters);
                        }
                        if (payload.message === 'connectionEstablished') {
                            setRemoteControlDialogOpen(false);
                            setDrawerOpen(false);
                            setCameraSettingsDialogOpen(false);
                            setMicrophoneSettingsDialogOpen(false);
                        }
                        break;
                    case 'currentParameters':
                        break;
                    case 'parameterUpdate':
                        const parameter: ParameterUpdate = JSON.parse(payload.message);
                        handleSetSceneParameters(parameter.boxIndex, parameter.parameterIndex, parameter.value);
                        break;
                    case 'sceneUpdate':
                        const sceneId = +payload.message;
                        handleCardClick(sceneId);
                        break;
                    case 'generalSettingsUpdate':
                        const generalSetting = payload.message as keyof GeneralSettings;
                        switch(generalSetting) {
                            case 'camera':
                                handleCameraButtonClick(!generalSettingsRef.current['camera']);
                                break;
                            case 'microphone':
                                handleMicrophoneButtonClick(!generalSettingsRef.current['microphone']);
                                break;
                            case 'cursorAnimation':
                                handleCursorAnimationButtonClick(!generalSettingsRef.current['cursorAnimation']);
                                break;
                            case 'skipTransitions':
                                handleSkipTransitionsButtonClick(!generalSettingsRef.current['skipTransitions']);
                                break;
                        }
                        break;
                    case 'audioSettingsUpdate':
                        const audioSetting = JSON.parse(payload.message) as AudioSettingUpdate;
                        handleMicrophoneSettingsUpdate(audioSetting.setting as keyof AudioSettings, audioSetting.value)
                }
            });
        
            socket.on('disconnect', () => {
                console.log('Socket disconnected');
            });

            const origin = window.location.origin === 'http://localhost:3001' ? 'http://192.168.1.161:3001' : window.location.origin;
            setSessionLink(`${origin}/remote/?sessionId=${sessionIdRef.current}`);
        
            return () => {
                socket.close();
            };
        }
    }, [sessionIdRef.current]);

    const handleStartSession = () => {
        const newSessionId = generateSecureId();
        sessionIdRef.current = newSessionId;
    };
    
    const handleRefreshSession = () => {
        const newSessionId = generateSecureId();
        socketRef.current!.emit('sessionChange', { oldSessionId: sessionIdRef.current, newSessionId });
        sessionIdRef.current = newSessionId;
    };

    const handleRemoteControlClick = () => {
        setRemoteControlDialogOpen(true);
        if (!sessionIdRef.current) handleStartSession();
    }

    const handleStopSession = () => {
        socketRef.current?.close();
        sessionIdRef.current = undefined;
        socketRef.current = undefined;
        setSessionLink(undefined);
    }

/*     const handleAnswerInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAnswer(event.target.value);
    };
    
    const handleSetAnswer = () => {
        if (peerConnection && answer) {
            const answerDesc = new RTCSessionDescription(JSON.parse(atob(answer)));
            peerConnection.setRemoteDescription(answerDesc).catch(console.error);
        }
    };
 */

    const handleScroll = (event: React.WheelEvent<HTMLDivElement>) => {
        if (event.deltaY !== 0) {
            event.currentTarget.scrollLeft += event.deltaY;
        }
    };

    return (
    <Drawer anchor={'bottom'} open={drawerOpen} onClose={() => setDrawerOpen(false) } 
        PaperProps={{style:  drawerPaperStyle }}
        hideBackdrop={true}
        ModalProps={{style: { pointerEvents: 'none' }}}
    >
        <Button
            onClick={() => setDrawerOpen(false)}
            style={{ ...drawerButtonStyle, top: -35 }}
        >
            <ExpandMore />
        </Button>
        <Box sx={{ display: 'flex', height: 250, width: '100%' }}>
            <Box sx={{ height: '100%', width: '220px', minWidth: '220px', padding: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-around', boxSizing: 'border-box' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                    <Tooltip title="Enable web camera for scenes requiring camera access" classes={{ tooltip: 'custom-tooltip', popper: 'custom-popper' }}>
                        <span style={{flexGrow: 1}}>
                            <ToggleButton
                                value="camera"
                                selected={cameraOn}
                                onChange={() => handleCameraButtonClick(!cameraOn)}
                                disabled={!mediaDevicesAvailable}
                                sx={{width: '100%'}}
                                size='small'
                                className={'toggle-button'}
                            >
                                <Typography>Camera</Typography>
                            </ToggleButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Camera settings" classes={{ tooltip: 'custom-tooltip', popper: 'custom-popper' }}>
                        <span>
                            <Button
                                onClick={() => handleCameraSettingsClick()}
                                disabled={!cameraOn}
                                className={'settings-button'}
                                size='small'
                            >
                                <Settings />
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Tooltip title="Enable microphone for audio reactive scenes" classes={{ tooltip: 'custom-tooltip', popper: 'custom-popper' }}>
                        <span style={{flexGrow: 1}}>
                            <ToggleButton
                                value="microphone"
                                selected={microphoneOn}
                                onChange={() => handleMicrophoneButtonClick(!microphoneOn)}
                                disabled={!mediaDevicesAvailable}
                                sx={{width: '100%'}}
                                size='small'
                                className={'toggle-button'}
                            >
                                <Typography>Microphone</Typography>
                            </ToggleButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Microphone settings" classes={{ tooltip: 'custom-tooltip', popper: 'custom-popper' }}>
                        <span>
                            <Button
                                onClick={() => handleMicrophoneSettingsClick()}
                                disabled={!microphoneOn}
                                className={'settings-button'}
                                size='small'
                            >
                                <Settings />
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
                <Box>
                    <Tooltip title="Enable special cursors for scenes. May reduce performance if experiencing frame drops" classes={{ tooltip: 'custom-tooltip', popper: 'custom-popper' }}>
                        <ToggleButton
                            value="cursorAnimation"
                            selected={cursorAnimationOn}
                            onChange={() => handleCursorAnimationButtonClick(!cursorAnimationOn)}
                            sx={{width: '100%'}}
                            size='small'
                            className={'toggle-button'}
                        >
                            <Typography>Cursor Animation</Typography>
                        </ToggleButton>
                    </Tooltip>
                </Box>
                <Box>
                    <Tooltip title="Skip fade transitions between scenes" classes={{ tooltip: 'custom-tooltip', popper: 'custom-popper' }}>
                        <ToggleButton
                            value="skipTransitions"
                            selected={skipTransitionsOn}
                            onChange={() => handleSkipTransitionsButtonClick(!skipTransitionsOn)}
                            sx={{width: '100%'}}
                            size='small'
                            className={'toggle-button'}
                        >
                            <Typography>Skip Transitions</Typography>
                        </ToggleButton>
                    </Tooltip>
                </Box>
                <Box>
                    <Tooltip title="Open extended settings dialogue" classes={{ tooltip: 'custom-tooltip', popper: 'custom-popper' }}>
                        <ToggleButton
                            value="remoteControl"
                            //selected={skipTransitionsOn}
                            onChange={() => handleRemoteControlClick()}
                            sx={{width: '100%'}}
                            size='small'
                            className={'toggle-button'}
                        >
                            <Typography>Remote Control</Typography>
                        </ToggleButton>
                    </Tooltip>
                </Box>
            </Box>
            <Divider orientation="vertical" variant="middle" flexItem sx={{  width: 2, marginLeft: '5px', marginRight: '5px' }} />

            <Box sx={{ height: '100%', flexGrow: 1, display: 'flex', minWidth: 0  }}>
                <Box sx={{ height: '100%', width: 50, display: 'flex'}} >
                    <Tabs
                        orientation="vertical"
                        value={tab}
                        onChange={handleTabChange}
                        sx={{ 
                            borderRight: 1, 
                            borderColor: 'divider',
                            '& .MuiTab-root': {
                                alignItems: 'flex-start',
                                flexDirection: 'row',
                                flexGrow: 1,
                                color: 'white',
                            },
                            '& .Mui-selected': {
                                color: 'white !important',
                            },
                            '& .MuiTabs-flexContainer': {
                                height: '100%',
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: 'white',
                            },
                        }}
                    >
                        <Tab label="Scenes" {...a11yProps(0)} sx={{writingMode: 'vertical-rl', transform: 'rotate(180deg)'}}/>
                        <Tab label="Parameters" {...a11yProps(1)} sx={{writingMode: 'vertical-rl', transform: 'rotate(180deg)'}} className={!hasClickedParameters ? 'blinking' : ''}/>
                    </Tabs>
                </Box>
                <Box
                    role="tabpanel"
                    hidden={tab !== 0}
                    id={`vertical-tabpanel-0`}
                    aria-labelledby={`vertical-tab-0`}
                    sx={{ height: '100%', flexGrow: 1, overflowX: 'auto' }}
                    onWheel={handleScroll}
                >
                    <Box sx={{ height: '100%', display: 'flex' }} role="presentation">
                        {scenes.map((card) => (
                            <Card 
                                key={card.id}
                                sx={{
                                    backgroundColor: scene === card.id ? 'rgb(90, 90, 90)' : 'rgb(75, 75, 75)',
                                    minWidth: 200,
                                    width: 200,
                                    margin: '10px 5px',
                                    flex: '0 0 auto',
                                    border: scene === card.id ? '2px solid white' : 'none',
                                }} 
                                elevation={4}
                                onClick={() => handleCardClick(card.id)}
                                >
                                <CardActionArea sx={{height: '100%', '&:hover': {backgroundColor: 'rgba(255, 255, 255, 0.1)'}}}>
                                    <CardMedia
                                    component="img"
                                    alt="scene"
                                    height="120"
                                    image={card.image}
                                    />
                                    <CardContent sx={{color: 'white'}}>
                                        <Typography variant="h5" component="div" align='center'>
                                            {card.title}
                                        </Typography>
                                        <Box 
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-evenly',
                                                marginTop: '10px'
                                            }}
                                        >
                                            <Tooltip title="Interactive" classes={{ tooltip: 'custom-tooltip', popper: 'custom-popper' }}>
                                                <SportsEsports sx={{ color: card.interactive ? 'white' : 'rgba(255, 255, 255, 0.3)' }} />
                                            </Tooltip>
                                            <Tooltip title="Requires Camera" classes={{ tooltip: 'custom-tooltip', popper: 'custom-popper' }}>
                                                <Videocam sx={{ color: card.videoResponsive ? 'white' : 'rgba(255, 255, 255, 0.3)' }} />
                                            </Tooltip>
                                            <Tooltip title="Audio Reactive" classes={{ tooltip: 'custom-tooltip', popper: 'custom-popper' }}>
                                                <MusicNote sx={{ color: card.audioReactive ? 'white' : 'rgba(255, 255, 255, 0.3)' }} />
                                            </Tooltip>
                                        </Box>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        ))}
                    </Box>
                </Box>
                <Box
                    role="tabpanel"
                    hidden={tab !== 1}
                    id={`vertical-tabpanel-1`}
                    aria-labelledby={`vertical-tab-1`}
                    sx={{ height: '100%', flexGrow: 1, overflowX: 'auto' }}
                    onWheel={handleScroll}
                >
                    {<SceneParametersControls scene={scene} sceneParameters={sceneParameters} microphoneOn={microphoneOn} cameraOn={cameraOn} handleSetSceneParameters={handleSetSceneParameters}/>}
                </Box>
            </Box>
        </Box>
        {mediaDevicesAvailable && <MicrophoneSettingsDialog isOpen={microphoneSettingsDialogOpen} microphoneSettings={microphoneSettings} handleClose={handleMicrophoneSettingsDialogClose} handleSettingsUpdate={handleMicrophoneSettingsUpdate}/>}
        {mediaDevicesAvailable && <CameraSettingsDialog isOpen={cameraSettingsDialogOpen} initialDeviceId={cameraId} handleClose={handleCameraSettingsDialogClose} handleChange={handleCameraChange}/>}
        <RemoteControlDialog sessionLink={sessionLink} remoteControlDialogOpen={remoteControlDialogOpen} handleRemoteControlDialogClose={handleRemoteControlDialogClose} handleRefreshSession={handleRefreshSession} handleStopSession={handleStopSession}/>
    </Drawer>
    )
})

/* interface DeviceSettingsDialogProps {
    deviceSettingsDialogOpen: {open: boolean, initial: string | undefined};
    deviceType: DeviceType;
    handleDeviceSettingsDialogClose: (deviceId: string | undefined) => void;
    handleDeviceChange: (deviceId: string | undefined) => void;
}

const DeviceSettingsDialog = ({deviceSettingsDialogOpen, deviceType, handleDeviceSettingsDialogClose, handleDeviceChange}: DeviceSettingsDialogProps) => {
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [currentDevice, setCurrentDevice] = useState<string | undefined>(deviceSettingsDialogOpen.initial);
    useEffect(() => {
        async function fetchCameras() {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === 'videoinput');
            setDevices(videoDevices);
            if (!deviceSettingsDialogOpen.initial) setCurrentDevice(videoDevices[0].deviceId);
            else setCurrentDevice(deviceSettingsDialogOpen.initial);
        }
        async function fetchMicrophones() {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const audioDevices = devices.filter(device => device.kind === 'audioinput');
            setDevices(audioDevices);
            if (!deviceSettingsDialogOpen.initial) setCurrentDevice(audioDevices[0].deviceId);
            else setCurrentDevice(deviceSettingsDialogOpen.initial);
        }
        if (deviceType == 'camera') fetchCameras();
        else fetchMicrophones();
    }, [deviceType, deviceSettingsDialogOpen]);
    return (
        <Dialog
            open={deviceSettingsDialogOpen.open}
            onClose={() => {
                handleDeviceSettingsDialogClose(deviceSettingsDialogOpen.initial);
                setCurrentDevice(deviceSettingsDialogOpen.initial);
            }}
            PaperProps={{
                component: 'form',
                onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
                    event.preventDefault();
                    const formData = new FormData(event.currentTarget);
                    const formJson = Object.fromEntries((formData as any).entries());
                    const deviceId = formJson.deviceId;
                    handleDeviceSettingsDialogClose(deviceId);
                },
            }}
        >
            <DialogTitle>Choose Device</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Please select your preferred device from the list below:
                </DialogContentText>
                <Select
                    value={currentDevice}
                    onChange={(event: SelectChangeEvent<string>) => {
                        const {target: { value }} = event;
                        setCurrentDevice(value);
                        handleDeviceChange(value);
                    }}
                    id="deviceId"
                    name="deviceId"
                    label="Choose device from list"
                    fullWidth
                    variant="standard"
                >
                    {devices.map((device) => (
                        <MenuItem
                            key={device.deviceId}
                            value={device.deviceId}
                        >
                            {device.label}
                        </MenuItem>
                    ))}
                </Select>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => {
                    handleDeviceSettingsDialogClose(deviceSettingsDialogOpen.initial);
                    setCurrentDevice(deviceSettingsDialogOpen.initial);
                }}>Cancel</Button>
                <Button type="submit">Change</Button>
            </DialogActions>
        </Dialog>
    );
} */

interface CameraSettingsDialogProps {
    isOpen: boolean;
    initialDeviceId: string;
    handleClose: (deviceId: string) => void;
    handleChange: (deviceId: string) => void;
}

const CameraSettingsDialog = ({isOpen, initialDeviceId, handleClose, handleChange}: CameraSettingsDialogProps) => {
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [currentDevice, setCurrentDevice] = useState<string>(initialDeviceId);
    const [initialDevice, setInitialDevice] = useState<string>(initialDeviceId);

    useEffect(() => {
        const fetchCameras = async () => {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === 'videoinput');
            setDevices(videoDevices);
            // Set the initial device when the dialog opens
            if (!initialDeviceId && videoDevices.length > 0) {
                setInitialDevice(videoDevices[0].deviceId);
                setCurrentDevice(videoDevices[0].deviceId);
            } else {
                setInitialDevice(initialDeviceId);
                setCurrentDevice(initialDeviceId);
            }
        };
        fetchCameras();
    }, [isOpen]);

    const handleCancel = () => {
        setCurrentDevice(initialDevice);
        handleClose(initialDevice);
    };

    return (
        <Dialog
            open={isOpen}
            onClose={() => handleClose(currentDevice)}
            PaperProps={{
                component: 'form',
                onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
                    event.preventDefault();
                    handleClose(currentDevice);
                },
            }}
        >
            <DialogTitle>Choose Camera</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Please select your preferred camera from the list below:
                </DialogContentText>
                <Select
                    value={currentDevice}
                    onChange={(event) => {
                        const value = event.target.value;
                        setCurrentDevice(value);
                        handleChange(value);
                    }}
                    fullWidth
                    variant="standard"
                >
                    {devices.map((device) => (
                        <MenuItem
                            key={device.deviceId}
                            value={device.deviceId}
                        >
                            {device.label}
                        </MenuItem>
                    ))}
                </Select>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button type="submit">Change</Button>
            </DialogActions>
        </Dialog>
    );
};

/* interface MicrophoneSettingsDialogProps {
    isOpen: boolean;
    initialDeviceId: string | undefined;
    handleClose: (deviceId: string | undefined) => void;
    handleChange: (deviceId: string | undefined) => void;
}
 */

interface MicrophoneSettings {
    currentDevice: string;
    bpm: number;
    threshold: number;
    frequencyRange: [number, number];
}

interface MicrophoneSettingsDialogProps {
    isOpen: boolean;
    microphoneSettings: MicrophoneSettings;
    handleClose: () => void;
    handleSettingsUpdate: (type: keyof MicrophoneSettings, value: number | number[] | string) => void;
}

const MicrophoneSettingsDialog = ({isOpen, microphoneSettings, handleClose, handleSettingsUpdate}: MicrophoneSettingsDialogProps) => {
    const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
    const [initialSettings, setInitialSettings] = useState<MicrophoneSettings | null>(null);

    // Effect to fetch devices and initialize settings when the dialog opens
    useEffect(() => {
        if (isOpen) {
            const fetchDevices = async () => {
                const availableDevices = await navigator.mediaDevices.enumerateDevices();
                const audioDevices = availableDevices.filter(device => device.kind === 'audioinput');
                setDevices(audioDevices);

                if (!microphoneSettings.currentDevice && audioDevices.length > 0) {
                    handleSettingsUpdate('currentDevice', audioDevices[0].deviceId);
                }
            };

            fetchDevices();
            setInitialSettings(microphoneSettings);
        }
    }, [isOpen]);

    const handleCancel = () => {
        if (initialSettings) {
            Object.entries(initialSettings).forEach(([key, value]) => {
                handleSettingsUpdate(key as keyof MicrophoneSettings, value);
            });
        }
        handleClose();
    };

    const handleSettingsChange = (type: keyof MicrophoneSettings, value: number | number[] | string) => {
        handleSettingsUpdate(type, value);
    };

    const scale = (x: number) => Math.log10(x + 10) * 1000;
    const valueLabelFormat = (value: number) => Math.pow(10, value / 1000) - 10;

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
        >
            <DialogTitle>Microphone Settings</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Please select your preferred microphone from the list below:
                </DialogContentText>
                <Select
                    value={microphoneSettings.currentDevice}
                    onChange={(event) => handleSettingsChange('currentDevice', event.target.value)}
                    id="deviceId"
                    name="deviceId"
                    label="Choose microphone"
                    fullWidth
                    variant="standard"
                >
                    {devices.map(device => (
                        <MenuItem key={device.deviceId} value={device.deviceId}>
                            {device.label || "Unnamed Device"}
                        </MenuItem>
                    ))}
                </Select>
                <FormControl fullWidth margin="normal" sx={{marginTop:'40px'}}>
                    <Typography variant="h6">Beats Per Minute (Set more than the actual BPM)</Typography>
                    <Slider
                        min={40}
                        max={300}
                        value={microphoneSettings.bpm}
                        onChange={(e, value) => handleSettingsChange('bpm', value as number)}
                        valueLabelDisplay="auto"
                    />
                </FormControl>
                <FormControl fullWidth margin="normal">
                    <Typography variant="h6">Threshold (Minimum Beat Volume)</Typography>
                    <Slider
                        min={0.05}
                        max={0.8}
                        step={0.01}
                        value={microphoneSettings.threshold}
                        onChange={(e, value) => handleSettingsChange('threshold', value as number)}
                        valueLabelDisplay="auto"
                    />
                </FormControl>
                <FormControl fullWidth margin="normal">
                    <Typography variant="h6">Beat Detection Frequency (Hz)</Typography>
                    <Slider
                        min={scale(20)}
                        max={scale(14000)}
                        step={10}
                        value={[scale(microphoneSettings.frequencyRange[0]), scale(microphoneSettings.frequencyRange[1])]}
                        onChange={(e, newValue) => {
                            // Explicitly check if newValue is an array before using map
                            if (Array.isArray(newValue)) {
                                handleSettingsChange('frequencyRange', newValue.map(valueLabelFormat) as [number, number]);
                            }
                        }}
                        valueLabelDisplay="auto"
                        valueLabelFormat={value => Math.round(valueLabelFormat(value))}
                        marks={[
                            {value: scale(20), label: '20 Hz'},
                            {value: scale(14000), label: '14000 Hz'}
                        ]}
                        sx={{
                            width: '90%', // Set the slider to 80% width
                            mx: 'auto',
                        }}
                    />
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button onClick={handleClose}>Save</Button>
            </DialogActions>
        </Dialog>
    );
};

interface RemoteControlDialogProps {
    sessionLink?: string;
    remoteControlDialogOpen: boolean;
    handleRemoteControlDialogClose: () => void;
    handleRefreshSession: () => void;
    handleStopSession: () => void;
}

const RemoteControlDialog = ({sessionLink, remoteControlDialogOpen, handleRemoteControlDialogClose, handleRefreshSession, handleStopSession}: RemoteControlDialogProps) => {
    const [sessionStopped, setSessionStopped] = useState(false);
    const stopSession = () => {
        setSessionStopped(true);
        handleStopSession();
    }
    const refreshSession = () => {
        setSessionStopped(false);
        handleRefreshSession();
    }
    return (
        <Dialog
            open={remoteControlDialogOpen}
            onClose={() => {
                handleRemoteControlDialogClose();
            }}
        >
            <DialogTitle>Remote Control</DialogTitle>
            <DialogContent sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}>
                {!sessionStopped && 
                    <DialogContentText width={400} paddingBottom={2}>
                        Use the QR code or the link below to open the remote control panel on another device.
                    </DialogContentText>
                }
                {sessionStopped && 
                    <DialogContentText>
                        Session Stopped.
                    </DialogContentText>
                }
                {sessionLink && (<QRCode value={sessionLink} size={400}/>)}
                {sessionLink && <Typography width={400} paddingTop={2} fontSize={10}>{sessionLink}</Typography>}
            </DialogContent>
            <DialogActions>
                {!sessionStopped && <Button variant="contained" color='error' onClick={stopSession}>Stop Connection</Button>}
                <Button variant="contained" color='info' onClick={refreshSession}>Refresh Connection</Button>
                <Button onClick={handleRemoteControlDialogClose}>Close Window</Button>
            </DialogActions>
        </Dialog>
    );
}

interface SceneParametersControlsProps {
    scene: number,
    sceneParameters: SceneParameters,
    cameraOn: boolean,
    microphoneOn: boolean,
    handleSetSceneParameters: (boxIndex: number, parameterIndex: number, value: number) => void
}

const SceneParametersControls = ({scene, sceneParameters, cameraOn, microphoneOn, handleSetSceneParameters}:SceneParametersControlsProps) => {
    return (
        <Box sx={{height: "100%", width: '100%', display: 'flex', justifyContent: 'flex-start', gap: '15px', padding: '15px', boxSizing: 'border-box'}}>
            {
                sceneParameters.map((propBox, index) => {
                    switch(propBox.category) {
                        case 'audio':
                            if (scenes[scene].audioReactive && microphoneOn) return <ParameterBox scene={scene} boxIndex={index} key={index} sceneParameters={sceneParameters} handleSetSceneParameters={handleSetSceneParameters}/>
                            break;
                        case 'video':
                            if (scenes[scene].videoResponsive && cameraOn) return <ParameterBox scene={scene} boxIndex={index} key={index} sceneParameters={sceneParameters} handleSetSceneParameters={handleSetSceneParameters}/>;
                            break;
                        case 'global':
                            return <ParameterBox scene={scene} boxIndex={index} key={index} sceneParameters={sceneParameters} handleSetSceneParameters={handleSetSceneParameters}/>;
                    }
                })
            }
        </Box>
    )
}

interface ParameterBoxProps {
    scene: number,
    boxIndex: number,
    sceneParameters: SceneParameters,
    handleSetSceneParameters: (boxIndex: number, parameterIndex: number, value: number) => void
}

export const ParameterBox = ({scene, boxIndex, sceneParameters, handleSetSceneParameters}: ParameterBoxProps) => {
    const parameters = sceneParameters[boxIndex].parameters;
    const boxName = sceneParameters[boxIndex].name;
    const boxType = sceneParameters[boxIndex].type;

    return (
        <Paper elevation={3} sx={{
            height: "100%",
            minWidth: '220px',
            maxWidth: '300px',
            padding: '5px 25px 10px 25px',
            backgroundColor: 'rgb(75, 75, 75)',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
        }}>
            <Typography variant="h6" component="h3" sx={{color: 'white', marginBottom: '5px'}} align='center'>{boxName}</Typography>
            { boxType == 'horizontalSlider' &&
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    flexGrow: 1,
                    width: '100%'
                }}>
                    {parameters.map((parameter, index) => (
                        <Box key={index}>
                            <Typography sx={{
                                color: 'white',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}> {parameter.name} </Typography>
                            <Slider
                                value={parameter.value}
                                onChange={(event, newValue) => {
                                    //parameter.value = newValue as number
                                    handleSetSceneParameters(boxIndex, index, newValue as number)
                                    /* setParameters(parameters.map((p, pi) => {
                                        if (pi === index) return parameter;
                                        else return p;
                                    })) */
                                }}
                                aria-labelledby="pattern-size-slider"
                                valueLabelDisplay="auto"
                                step={0.01}
                                min={0}
                                max={1}
                                sx={{
                                    color: 'white',
                                    '& .MuiSlider-thumb': {
                                        '&:hover, &.Mui-focusVisible': {
                                            boxShadow: `0px 0px 0px 8px rgba(255, 255, 255, 0.16)`
                                        },
                                        '&.Mui-active': {
                                            boxShadow: `0px 0px 0px 14px rgba(255, 255, 255, 0.16)`
                                        }
                                    },
                                }}
                            />
                        </Box>
                    ))}
                </Box>
            }
            
            { boxType == 'toggle' &&
                <Box sx={{
                    flexGrow: 1,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 0.5fr)',
                    gap: 1,
                    width: '100%',
                }}>
                    {parameters.map((parameter, index) => (
                        <Box 
                            key={index}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%'
                            }}>
                            <Typography sx={{
                                color: 'white',
                                display: '-webkit-box',
                                WebkitLineClamp: 2, // Limits text to two lines
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '100%',
                                textAlign: 'center'
                            }}> {parameter.name} </Typography>
                            <Switch
                            checked={!!parameter.value}
                            onChange={(event, newValue) => {
                                //parameter.value = +newValue as number
                                handleSetSceneParameters(boxIndex, index, +newValue as number)
                                /* setParameters(parameters.map((p, pi) => {
                                    if (pi === index) return parameter;
                                    else return p;
                                })) */
                            }}
                            aria-labelledby="pattern-size-slider"
                            sx={{
                                '& .MuiSwitch-thumb': {
                                    color: 'white !important', 
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.8) !important',
                                    },
                                },
                                '& .MuiSwitch-track': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1) !important',
                                },
                                '& .Mui-checked .MuiSwitch-thumb': {
                                    color: 'white !important',
                                },
                                '& .Mui-checked + .MuiSwitch-track': {
                                    opacity: 1,
                                    backgroundColor: 'white !important',
                                },
                                '& .MuiSwitch-thumb.Mui-focusVisible, & .MuiSwitch-thumb.Mui-active': {
                                    boxShadow: `0px 0px 0px 14px rgba(255, 255, 255, 0.16) !important`,
                                }
                            }}/>
                        </Box>
                    ))}
                </Box>
            }
        </Paper>
    )
}